import {Banner} from '@primer/react'
import {useSessionContext} from '../../contexts/SessionContext'
import {useAgentInfo} from '@github-ui/copilot-shared-ui/agents/use-agent-info'
import {useAgentQuotaSnapshot} from '../../hooks/use-agent-quota-snapshot'
import {renderTextWithLinks} from '../../utils/render-text-with-links'
import {CopilotPlan} from '@github-ui/copilot-chat-types'
import {MANAGE_BUDGET_URL} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'

interface SessionCancelledBannerProps {
  cancellationMessage: string | null
  workflowRunLink: string | null
}

const DEFAULT_CANCELLATION_MESSAGE = 'The session was cancelled.'
const QUOTA_CANCELLATION_MESSAGE_INDIVIDUAL =
  "The session was cancelled because you don't have enough AI credits to continue. Top up your quota or wait for it to reset to start a new session."
const QUOTA_CANCELLATION_MESSAGE_ADMIN_MANAGED =
  "The session was cancelled because you don't have enough AI credits to continue. Contact your organization admin to request additional usage."

function isAdminManagedPlan(plan: string | undefined): boolean {
  return plan === CopilotPlan.Business || plan === CopilotPlan.Enterprise
}

export function SessionCancelledBanner({cancellationMessage, workflowRunLink}: SessionCancelledBannerProps) {
  const {session} = useSessionContext()
  const {data: agentInfo} = useAgentInfo(session.agent_id, session.agent_slug)

  // The runtime in-session quota check cancels the session without attaching
  // an error message (see github/sweagentd#12390). When that happens we fetch
  // the user's current quota snapshot so we can confirm the quota reason and
  // surface a plan-appropriate action (Manage Budget for individuals,
  // contact-admin copy for business / enterprise). `enabled` keeps this off
  // for cancels that came with a specific message (e.g. user-initiated cancel).
  const {data: agentQuotaSnapshot} = useAgentQuotaSnapshot({enabled: !cancellationMessage})

  const isQuotaCancellation = !cancellationMessage && agentQuotaSnapshot?.can_start_session === false
  const adminManaged = isAdminManagedPlan(agentQuotaSnapshot?.copilot_plan)

  const message = isQuotaCancellation
    ? adminManaged
      ? QUOTA_CANCELLATION_MESSAGE_ADMIN_MANAGED
      : QUOTA_CANCELLATION_MESSAGE_INDIVIDUAL
    : cancellationMessage || DEFAULT_CANCELLATION_MESSAGE

  // For individual quota cancellations, surface Manage budget as the primary
  // action — admin-managed plans can't self-serve, so we direct them to their
  // admin via inline copy instead. The workflow-run link, when present, is
  // always surfaced as the secondary action so users can dig into the actions
  // run regardless of plan.
  const manageBudgetAction = (
    <Banner.PrimaryAction as="a" href={MANAGE_BUDGET_URL}>
      Manage budget
    </Banner.PrimaryAction>
  )
  const viewRunPrimaryAction = workflowRunLink ? (
    <Banner.PrimaryAction as="a" href={workflowRunLink}>
      View run
    </Banner.PrimaryAction>
  ) : null
  const viewRunSecondaryAction = workflowRunLink ? (
    <Banner.SecondaryAction as="a" href={workflowRunLink}>
      View run
    </Banner.SecondaryAction>
  ) : null

  const showManageBudget = isQuotaCancellation && !adminManaged
  const primaryAction = showManageBudget ? manageBudgetAction : (viewRunPrimaryAction ?? undefined)
  const secondaryAction = showManageBudget ? (viewRunSecondaryAction ?? undefined) : undefined

  return (
    <Banner
      aria-label="Session cancelled"
      title={`${agentInfo?.display_name || 'Agent'}'s work was cancelled`}
      description={renderTextWithLinks(message)}
      variant="warning"
      actionsLayout="stacked"
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
    />
  )
}
